import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    Signal,
    signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { AddEntityButtonComponent } from '@connect-services/client/components/add-entity-button/add-entity-button.component';
import {
    MapBaseSource,
    MapFieldInterventionSource,
    MapLayerSource,
    MapSourceTypeEnum,
} from '@connect-services/client/components/map/map.component';
import { SourceHeaderComponent } from '@connect-services/client/components/sources/source-header/source-header.component';
import { SourceItemComponent } from '@connect-services/client/components/sources/source-item/source-item.component';
import { SourceThemeComponent } from '@connect-services/client/components/sources/source-theme/source-theme.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
    OpenCreateEntityPayload,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { BroadcastChannelService } from '@connect-services/client/services/broadcast-channel.service';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { FieldInterventionService } from '@connect-services/client/services/field-intervention.service';
import { HasPermissionsService, OperatorEnum } from '@connect-services/client/services/has-permissions.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { GeomFilter } from '@connect-services/client/services/layer.service';
import { CUSTOM_INDICATOR_SOURCE_ID } from '@connect-services/client/services/map-click-indicator.service';
import { MapDrawService } from '@connect-services/client/services/map-draw.service';
import { LayerEditUiConfig, MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { SourcesActivationService } from '@connect-services/client/services/source-activation.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { SourcesVisibilityService, SourceTypeEnum } from '@connect-services/client/services/sources-visibility.service';
import { UrlSourceEnum } from '@connect-services/client/services/url-source.service';
import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import {
    DepositsService,
    Layer,
    LayerControllerGetRequestParams,
    LayersService,
    LayerTopic,
    LayerTopicsService,
    MapboxStyle,
    PermissionAliasEnum,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import {
    defaultPaginationParameters,
    OrderEnum,
    paginationMaxTake,
    PaginationParameters,
} from '@shared/client/models/pagination.model';

export type LayerSourcesTheme = {
    groups: MapLayerSource[][];
    theme: LayerTopic;
};

export enum ViewContextEnum {
    MAP = 'MAP',
    TABLE_INTERNAL = 'TABLE_INTERNAL',
    TABLE_EXTERNAL = 'TABLE_EXTERNAL',
}

const UNGROUPED_THEME = 'ungrouped';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AddEntityButtonComponent,
        ExpansionPanelComponent,
        RessourcesModule,
        SourceHeaderComponent,
        SourceItemComponent,
        SourceThemeComponent,
    ],
    selector: 'app-sources',
    styleUrl: './sources.component.scss',
    templateUrl: './sources.component.html',
})
export class SourcesComponent {
    public readonly layerEditConfigForLayerSources = computed<LayerEditUiConfig | null>(() => {
        return this.mapEditModeService.getLayerEditConfig({
            showAddEntity: true,
            showGeographicEdit: true,
        });
    });
    public readonly tabularLayerEditConfig = computed<LayerEditUiConfig | null>(() => {
        return this.mapEditModeService.getLayerEditConfig({
            showAddEntity: true,
            showGeographicEdit: false,
        });
    });

    protected readonly areLayersGrouped = signal<boolean>(true);
    protected readonly disabledSourcesCount = computed(() => {
        MapSourceService.sourcesActivationChanged();

        const allSources: MapLayerSource[] = [...this.layerSources(), ...this.interstandardSources()];

        return this.areLayersGrouped()
            ? this.countDisabledGroups(allSources)
            : allSources.filter((s) => !s.isActivated).length;
    });
    protected readonly fieldInterventionSources = signal<MapFieldInterventionSource[]>([]);
    protected readonly interstandardByTheme: WritableSignal<Record<string, MapLayerSource[]>> = signal<
        Record<string, MapLayerSource[]>
    >({});
    protected readonly interstandardSources: WritableSignal<MapLayerSource[]> = signal<MapLayerSource[]>([]);
    protected readonly interstandardSourcesByTheme: Signal<LayerSourcesTheme[]> = computed(() => {
        const themeIndex = new Map(this.sourcesTheme().map((theme) => [theme.id, theme]));
        const themeMap: Record<string, MapLayerSource[]> = this.interstandardByTheme();

        const entries = this.buildLayerSourcesThemeEntries(themeMap, themeIndex);

        return entries.sort((a, b) => this.compareThemes(a, b));
    });
    protected readonly interstandardWithoutTheme: Signal<MapLayerSource[]> = computed(() => {
        const ungroupedList: MapLayerSource[] = this.interstandardByTheme()[UNGROUPED_THEME] ?? [];

        if (!this.areLayersGrouped()) {
            return this.sortLayersInsideTheme(ungroupedList);
        }

        return this.buildByName(ungroupedList);
    });
    protected readonly layerGroupedSourcesByTheme: WritableSignal<Record<string, MapLayerSource[]>> = signal<
        Record<string, MapLayerSource[]>
    >({});
    protected readonly layerSources: WritableSignal<MapLayerSource[]> = signal<MapLayerSource[]>([]);
    protected readonly layerSourcesByTheme: Signal<LayerSourcesTheme[]> = computed(() => {
        const themeIndex = new Map(this.sourcesTheme().map((theme: LayerTopic) => [theme.id, theme]));

        const themeMap: Record<string, MapLayerSource[]> = this.layerUngroupedSourcesByTheme();
        const entries = this.buildLayerSourcesThemeEntries(themeMap, themeIndex);

        return entries.sort((a: LayerSourcesTheme, b: LayerSourcesTheme) => this.compareThemes(a, b));
    });
    protected readonly layerUngroupedSourcesByTheme: WritableSignal<Record<string, MapLayerSource[]>> = signal<
        Record<string, MapLayerSource[]>
    >({});
    protected readonly noGeomLayers = signal<Layer[]>([]);
    protected readonly noGeomLayersGroupedSource = computed((): Layer[] =>
        this.noGeomLayers().reduce((acc: Layer[], currentLayer: Layer): Layer[] => {
            if (acc.findIndex((layer) => layer.name === currentLayer.name) !== -1) {
                return acc;
            }
            acc.push(currentLayer);

            return acc;
        }, []),
    );
    protected readonly sourcesTheme: WritableSignal<LayerTopic[]> = signal<LayerTopic[]>([]);
    protected readonly sourcesWithoutTheme = computed<MapLayerSource[][]>(() => {
        const layerList: MapLayerSource[] = this.layerSources().filter((source: MapLayerSource) => !source.layerTopic);

        if (!this.areLayersGrouped()) {
            return [...layerList.map((layerSource: MapLayerSource) => [layerSource])];
        }

        const groupedLayers: {
            [layerName: string]: MapLayerSource[];
        } = {};
        for (const layerSource of layerList) {
            if (!groupedLayers[layerSource.name]) {
                groupedLayers[layerSource.name] = [];
            }

            groupedLayers[layerSource.name].push(layerSource);
        }

        return Object.values(groupedLayers);
    });
    protected readonly urlLayerSources = signal<MapLayerSource[]>([]);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly Layer = Layer;
    protected readonly mapDrawService = inject(MapDrawService);
    protected readonly mapEditModeService = inject(MapEditModeService);
    protected readonly projectEditModeService = inject(ProjectEditModeService);
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly sourcesService = inject(SourcesService);
    protected readonly sourcesVisibilityService = inject(SourcesVisibilityService);
    protected readonly SourceTypeEnum = SourceTypeEnum;

    private readonly broadcastChannelService = inject(BroadcastChannelService);
    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly depositsService = inject(DepositsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly fetchAllService = inject(FetchAllService);
    private readonly fieldInterventionsService: FieldInterventionService = inject(FieldInterventionService);
    private readonly hasPermissionsService = inject(HasPermissionsService);
    private readonly homeService = inject(HomeService);
    private readonly projectBloc: ProjectBloc = inject(ProjectBloc);
    private readonly sdkLayersService = inject(LayersService);
    private readonly sdkLayerTopicsService = inject(LayerTopicsService);
    private readonly sourcesActivationService = inject(SourcesActivationService);

    public constructor() {
        this.destroyRef.onDestroy(() => this.fieldInterventionsService.resetHasEntities());

        effect(() => this.handleSourcesChange());
        effect(() => this.getDepositNamesForLayerSources());
        effect(() => this.loadNoGeomLayers());
    }

    protected async handleAddEntity(layerId: string, layerName: string): Promise<void> {
        const currentView: ViewContextEnum = this.getCurrentView();

        switch (currentView) {
            case ViewContextEnum.MAP:
                await this.handleAddEntityFromMap(layerId, layerName);
                break;

            case ViewContextEnum.TABLE_EXTERNAL:
            case ViewContextEnum.TABLE_INTERNAL:
                this.broadcastOpenCreateEntity(layerId, layerName);
                break;
        }
    }

    protected handleLayersVisibility(areLayersVisible: boolean): void {
        this.sourcesVisibilityService.changeAllSourcesVisibility(areLayersVisible);
    }

    protected isFieldInterventionDisabled(source: MapBaseSource<unknown>): boolean {
        return source instanceof MapFieldInterventionSource && !this.fieldInterventionsService.hasEntities();
    }

    protected openLayerInExternalWindow(event: MouseEvent, layerName: string): void {
        event.stopPropagation();

        this.openLayerExternalView(layerName);
    }

    protected updateLayersGrouped(areLayersGrouped: boolean): void {
        this.areLayersGrouped.set(areLayersGrouped);
        if (areLayersGrouped) {
            return;
        }
        this.generalizeVisibilityOnGroup(this.layerUngroupedSourcesByTheme());
        this.generalizeVisibilityOnGroup(this.interstandardByTheme());
    }

    private broadcastOpenCreateEntity(layerId: string, layerName: string): void {
        const channel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);
        channel.postMessage(<BroadcastChannelEvent<OpenCreateEntityPayload>>{
            additionalData: {
                layerId,
                layerName,
            },
            message: BroadcastChannelMessageEnum.OPEN_CREATE_ENTITY,
            source: BroadcastChannelSourceEnum.LAYER,
        });

        channel.close();
    }

    private buildByName(list: MapLayerSource[]): MapLayerSource[] {
        const layerByName: Record<string, MapLayerSource[]> = this.groupByName(list);

        const mapLayerSources: MapLayerSource[] = Object.values(layerByName).map(
            (sameNameList: MapLayerSource[]): MapLayerSource => {
                const principalLayer: MapLayerSource = this.pickPrincipal(sameNameList);
                const uniqueStyles: MapboxStyle[] = this.setUniqueStyle(sameNameList);
                const groupVisible: boolean = this.isGroupVisible(sameNameList);

                return {
                    ...principalLayer,
                    isActivated: this.mapDrawService.setIsActivated(principalLayer),
                    isVisible: groupVisible,
                    style: uniqueStyles,
                };
            },
        );

        return this.sortLayersInsideTheme(mapLayerSources);
    }

    private buildGroupsByName(list: MapLayerSource[]): MapLayerSource[][] {
        const byName: Record<string, MapLayerSource[]> = this.groupByName(list);

        const groups: MapLayerSource[][] = Object.values(byName).map((sameNameList) => {
            return this.sortSourcesByOrder(sameNameList);
        });

        return groups.sort((a: MapLayerSource[], b: MapLayerSource[]) => a[0].name.localeCompare(b[0].name));
    }

    private buildLayerSourcesByName(sources: MapLayerSource[]): Record<string, MapLayerSource[]> {
        const layersByName: Record<string, MapLayerSource[]> = this.groupByLayer(sources);

        const result: Record<string, MapLayerSource[]> = {};

        for (const [themeId, list] of Object.entries(layersByName)) {
            if (themeId === UNGROUPED_THEME) {
                result[themeId] = this.sortSourcesByOrder(list);
                continue;
            }

            const byName: Record<string, MapLayerSource[]> = list.reduce(
                (acc: Record<string, MapLayerSource[]>, source: MapLayerSource) => {
                    if (!acc[source.name]) {
                        acc[source.name] = [];
                    }
                    acc[source.name].push(source);

                    return acc;
                },
                {},
            );

            const representatives: MapLayerSource[] = Object.values(byName).map((sameNameList: MapLayerSource[]) => {
                const sorted: MapLayerSource[] = this.sortSourcesByOrder(sameNameList);

                return sorted[0];
            });

            result[themeId] = this.sortSourcesByOrder(representatives);
        }

        return result;
    }

    private buildLayerSourcesThemeEntries(
        themeMap: Record<string, MapLayerSource[]>,
        themeIndex: Map<string, LayerTopic>,
    ): LayerSourcesTheme[] {
        const entries: LayerSourcesTheme[] = [];
        const groupedMode = this.areLayersGrouped();

        for (const [key, layerList] of Object.entries(themeMap)) {
            if (key === UNGROUPED_THEME || !layerList?.length) {
                continue;
            }

            const theme = themeIndex.get(key);
            if (!theme) {
                continue;
            }

            const groups: MapLayerSource[][] = groupedMode
                ? this.buildGroupsByName(layerList)
                : this.sortLayersInsideTheme(layerList).map((source) => [source]);

            entries.push({ groups, theme });
        }

        return entries;
    }

    private categorizeSourcesByType(sources: MapBaseSource<unknown>[]): {
        fieldInterventionSources: MapFieldInterventionSource[];
        interstandardSources: MapLayerSource[];
        noUrlSources: MapLayerSource[];
        urlSources: MapLayerSource[];
    } {
        const urlSources: MapLayerSource[] = [];
        const noUrlSources: MapLayerSource[] = [];
        const interstandardSources: MapLayerSource[] = [];
        const fieldInterventionSources: MapFieldInterventionSource[] = [];

        sources.forEach((source: MapBaseSource<unknown>) => {
            if (source.sourceId === CUSTOM_INDICATOR_SOURCE_ID) {
                return;
            }

            if (source instanceof MapFieldInterventionSource) {
                fieldInterventionSources.push(source);

                return;
            }

            if (source.type !== MapSourceTypeEnum.LAYER) {
                return;
            }

            const layerSource = source as MapLayerSource;
            const isUrlType = Object.values(UrlSourceEnum).includes(layerSource.data.type as UrlSourceEnum);

            if (isUrlType) {
                urlSources.push(layerSource);
            } else if (layerSource.data.type === Layer.TypeEnum.INTERSTANDARD) {
                interstandardSources.push(layerSource);
            } else {
                noUrlSources.push(layerSource);
            }
        });

        return {
            fieldInterventionSources,
            interstandardSources,
            noUrlSources: noUrlSources,
            urlSources,
        };
    }

    private compareThemes(a: LayerSourcesTheme, b: LayerSourcesTheme): number {
        const orderA: number | undefined = a.theme.order;
        const orderB: number | undefined = b.theme.order;

        if (orderA != null && orderB != null && orderA !== orderB) {
            return orderA - orderB;
        }
        if (orderA != null && orderB === null) {
            return -1;
        }
        if (orderA === null && orderB != null) {
            return 1;
        }

        return a.theme.name.localeCompare(b.theme.name);
    }

    private countDisabledGroups(sources: MapLayerSource[]): number {
        const groupedLayers: Record<string, MapLayerSource[]> = this.groupByName(sources);
        let disabledGroups = 0;

        for (const list of Object.values(groupedLayers)) {
            if (!list.length) {
                continue;
            }

            const allInactive = list.every((source) => !this.sourcesActivationService.get(source.sourceId));
            if (allInactive) {
                disabledGroups++;
            }
        }

        return disabledGroups;
    }

    private generalizeVisibilityOnGroup(themeMap: Record<string, MapLayerSource[]>): void {
        Object.entries(themeMap).forEach(([themeId, layerList = []]: [string, MapLayerSource[]]) => {
            if (themeId === UNGROUPED_THEME || !layerList.length) {
                return;
            }

            const byName: Record<string, MapLayerSource[]> = this.groupByName(layerList);

            Object.values(byName).forEach((sameNameGroup: MapLayerSource[]) => {
                const principal: MapLayerSource = this.pickPrincipal(sameNameGroup);
                const visible: boolean = this.sourcesVisibilityService.isVisible(principal);
                const targets: MapLayerSource[] = sameNameGroup.filter((source: MapLayerSource) => !source.isFiltered);
                this.sourcesVisibilityService.setSourcesVisibility(targets, visible);
            });
        });
    }

    private getCurrentView(): ViewContextEnum {
        if (this.homeService.isExternalPage()) {
            return ViewContextEnum.TABLE_EXTERNAL;
        }

        if (this.contentLayoutService.isDataTableView()) {
            return ViewContextEnum.TABLE_INTERNAL;
        }

        return ViewContextEnum.MAP;
    }

    private async getDepositName(depositId: string): Promise<string> {
        const requiredPermissions = [PermissionAliasEnum.READ_DEPOSIT, PermissionAliasEnum.MANAGE_DEPOSIT];

        if (!this.hasPermissionsService.checkPermissions(requiredPermissions, OperatorEnum.OR)) {
            return '';
        }

        const deposit = await firstValueFrom(this.depositsService.depositControllerGetOne({ id: depositId }));

        return deposit.name;
    }

    private async getDepositNamesForLayerSources(): Promise<void> {
        if (!this.layerSources().length) {
            return;
        }

        const uniqueDepositIds: string[] = [
            ...new Set(
                this.layerSources()
                    .map((source) => source.data?.deposit)
                    .filter(Boolean) as string[],
            ),
        ];

        const depositNameMap = new Map<string, string>();
        await Promise.all(
            uniqueDepositIds.map(async (depositId: string) => {
                const depositName = await this.getDepositName(depositId);
                depositNameMap.set(depositId, depositName);
            }),
        );

        this.layerSources().forEach((source: MapLayerSource) => {
            const depositId: string | null = source.data?.deposit;
            if (depositId) {
                untracked(() => {
                    source.depositName = depositNameMap.get(depositId);
                });
            }
        });
    }

    private async getSourcesTheme(): Promise<void> {
        const paginationParams: PaginationParameters = {
            ...defaultPaginationParameters,
            order: OrderEnum.ASC,
            orderBy: 'name',
            take: paginationMaxTake,
        };

        let hasNextPage = false;
        const allSourcesTheme: LayerTopic[] = [];

        do {
            const paginated = await firstValueFrom(
                this.sdkLayerTopicsService.layerTopicControllerGet({
                    ...paginationParams,
                    projectId: this.projectBloc.value()!.id,
                }),
            );
            allSourcesTheme.push(...paginated.items);
            paginationParams.page += 1;
            hasNextPage = paginated.hasNextPage;
        } while (hasNextPage);

        this.sourcesTheme.set(allSourcesTheme);
    }

    private groupByLayer<T extends { layerTopic?: LayerTopic | string | number | null }>(
        items: T[],
    ): Record<string, T[]> {
        return items.reduce((acc: Record<string, T[]>, item: T) => {
            const key = String(item.layerTopic ?? UNGROUPED_THEME);

            if (acc[key] == null) {
                acc[key] = [];
            }
            acc[key].push(item);

            return acc;
        }, {});
    }

    private groupByName(list: MapLayerSource[]): Record<string, MapLayerSource[]> {
        return list.reduce<Record<string, MapLayerSource[]>>(
            (acc: Record<string, MapLayerSource[]>, source: MapLayerSource) => {
                if (!acc[source.name]) {
                    acc[source.name] = [];
                }
                acc[source.name].push(source);

                return acc;
            },
            {},
        );
    }

    private async handleAddEntityFromMap(layerId: string, layerName: string): Promise<void> {
        const exists = await this.broadcastChannelService.checkIfLayerWindowExists(layerName);
        if (exists) {
            this.broadcastOpenCreateEntity(layerId, layerName);

            return;
        }

        const channel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);

        channel.onmessage = (event: MessageEvent<BroadcastChannelEvent<OpenCreateEntityPayload | null>>): void => {
            if (event.data?.message !== BroadcastChannelMessageEnum.TABLE_READY) {
                return;
            }

            this.broadcastOpenCreateEntity(layerId, layerName);

            channel.close();
        };

        this.openLayerExternalView(layerName);
    }

    private async handleSourcesChange(): Promise<void> {
        const sources: MapBaseSource<unknown>[] = this.sourcesService.sources();

        if (!sources.length) {
            this.resetSources();

            return;
        }

        const { urlSources, noUrlSources, interstandardSources, fieldInterventionSources } =
            this.categorizeSourcesByType(sources);

        this.urlLayerSources.set(this.sortSourcesByOrder(urlSources));
        await this.getSourcesTheme();

        const sortedNoUrlSources: MapLayerSource[] = this.sortSourcesByOrder(noUrlSources);
        this.layerSources.set(sortedNoUrlSources);

        this.layerUngroupedSourcesByTheme.set(this.groupByLayer(sortedNoUrlSources));
        this.layerGroupedSourcesByTheme.set(this.buildLayerSourcesByName(sortedNoUrlSources));

        const sortedInterstandardSources: MapLayerSource[] = this.sortSourcesByOrder(interstandardSources);
        this.interstandardSources.set(sortedInterstandardSources);
        this.interstandardByTheme.set(this.groupByLayer(sortedInterstandardSources));

        await this.manageFieldInterventionSources(fieldInterventionSources);
    }

    private isGroupVisible(list: MapLayerSource[]): boolean {
        return list.some(
            (source: MapLayerSource) => this.sourcesVisibilityService.isVisible(source) && !source.isFiltered,
        );
    }

    private async loadNoGeomLayers(): Promise<void> {
        const layers: Layer[] = await this.fetchAllService.getAll<LayerControllerGetRequestParams, Layer>(
            this.sdkLayersService.layerControllerGet.bind(this.sdkLayersService),
            {
                geomFilter: GeomFilter.ONLY_NOGEOM,
                project: this.projectBloc.value()!.id,
            },
        );

        this.noGeomLayers.set(layers);
    }

    private async manageFieldInterventionSources(sources: MapFieldInterventionSource[]): Promise<void> {
        const fieldInterventionPromises = sources
            .filter((source: MapFieldInterventionSource) => source.type === MapSourceTypeEnum.FIELD_INTERVENTION)
            .map((source: MapFieldInterventionSource) => this.fieldInterventionsService.setHasEntities(source.data));

        await Promise.all(fieldInterventionPromises);
        this.fieldInterventionSources.set(sources);
    }

    private openLayerExternalView(layerName: string): void {
        const url = document.URL;
        const pathSuffix = `/${RoutePathEnum.LAYERS}`;
        const finalURL = url.slice(0, url.length - pathSuffix.length);

        this.broadcastChannelService.openInNewWindow(finalURL, {
            ext: 'true',
            layerName: layerName,
        });
    }

    private pickPrincipal(layers: MapLayerSource[]): MapLayerSource {
        return this.sortSourcesByOrder(layers)[0];
    }

    private resetSources(): void {
        this.layerSources.set([]);
        this.interstandardSources.set([]);
        this.fieldInterventionSources.set([]);
        this.urlLayerSources.set([]);
    }

    private setUniqueStyle(list: MapLayerSource[]): MapboxStyle[] {
        const map = new Map<string, MapboxStyle>();

        list.flatMap((layer: MapLayerSource): MapboxStyle[] => layer.style ?? []).forEach((style: MapboxStyle) => {
            const key = JSON.stringify(style);
            if (map.has(key)) {
                return;
            }
            map.set(key, style);
        });

        return Array.from(map.values());
    }

    private sortLayersInsideTheme<T extends MapLayerSource>(list: T[]): T[] {
        return list.toSorted((a: MapLayerSource, b: MapLayerSource) => a.name.localeCompare(b.name));
    }

    private sortSourcesByOrder<T extends MapLayerSource>(sources: T[]): T[] {
        return sources.toSorted((a: MapLayerSource, b: MapLayerSource) => b.data.order - a.data.order);
    }
}
